<script lang="ts">
export default {
  name: 'MenuActions'
};
</script>

<script setup lang="ts">
import { Store } from 'vuex';
import { AppState } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { useRouter } from 'vue-router';
import { computed, inject, ref, watch } from 'vue';
import { ChevronsLeft, Users } from '@lucide/vue';
import { towerService } from '@/services/v2/TowerService';
import { Contributor } from '@/types/tower/v2/Contributor';

const store = inject<Store<AppState>>('store');
const router = useRouter();
const loading = ref<boolean>(true);
const showContributors = ref<boolean>(false);
const contributors = ref<Contributor[]>([]);

// ---------------
// watchers
// ---------------
watch(
  () => store?.state.towerCanvasState.currentTowerInstance?.towerId,
  async (newTowerId) => {
    if (newTowerId) {
      loading.value = true;
      contributors.value = await fetchTowerContributors(newTowerId);
      loading.value = false;
    }
  },
  { immediate: true }
);


// ---------------
// computed
// ---------------
const accountName = computed(() => {
  return store?.state.towerCanvasState.accountName ?? '';
});

const programName = computed(() => {
  return store?.state.towerCanvasState.programName ?? '';
});

const versionLabel = computed(() => {
  return store?.state.towerCanvasState.currentTowerInstance?.versionLabel ?? '';
});

const towerName = computed(() => {
  return store?.state.towerCanvasState.currentTowerInstance?.towerName ?? '';
});


// ---------------
// methods
// ---------------
function handleBack() {
  router.back();
}

function handleProgramClick() {
  store?.commit(StoreMutationType.SET_PROGRAMS_PANEL_STATE, {
    show: true,
    tab: 'versions'
  });
}

async function fetchTowerContributors(towerId: string) {
  loading.value = true;

  const contributors = await towerService.getTowerContributors(towerId);
  loading.value = false;
  return contributors;
}


</script>

<template>
  <div class="flex gap-2 justify-self-start justify-start ml-2 mt-2 h-10">
    
    <div @click="handleBack" class="flex-nowrap bg-tower-alt
      border border-solid border-tower-border
      flex items-center justify-items-center
      cursor-pointer pointer-events-auto
      border-box rounded-md shadow-lg
      hover:bg-tower-hover px-1 py-1">
      
      <ChevronsLeft class="scale-75" />
      <span class="mr-2">Back</span>
    </div>

    <div v-if="contributors && contributors.length > 0" class="relative flex flex-col justify-center ml-2 pointer-events-auto cursor-pointer">
      <Users
        :size="20"
        class="text-gray-700 self-end pointer-events-auto"
        @mouseover="showContributors = true" 
        @mouseleave="showContributors = false"
      />
      <div v-show="showContributors" 
        class="absolute whitespace-nowrap border border-gray-300 bg-white shadow-md px-4 py-2 rounded-md z-10 left-0 top-7 mt-1">
        <p class="text-sm font-bold mb-1">Contributors</p>
        <ul class="list-none m-0 p-0">
          <li v-for="(contributor, index) in contributors" :key="index" class="text-sm">
            {{ contributor.name !== '' ? contributor.name : contributor.email }}
          </li>
        </ul>
      </div>
    </div>
    
    <div class="flex items-center px-2 cursor-pointer pointer-events-auto text-center">
      
      <div @click="handleProgramClick"
           class="flex flex-row gap-3 text-center items-center" :title="versionLabel ?? 'Initial Version'">
        <span class="font-bold">{{ accountName }}</span>
        <span class="text-xs">{{ programName !== '' ? programName : towerName }}</span>
      </div>
    </div>
  </div>
</template>

<style>
</style>
